import { useEffect, useState, type ImgHTMLAttributes } from "react";

/**
 * SafeImage — an <img> that survives a broken URL.
 *
 * If the primary src fails to load, walks through any additional
 * sources before falling back to a locally-served brand photograph.
 * If that also fails, the image element is removed and the parent's
 * background colour shows through, so cards never display a
 * broken-image icon or alt text in a grey box.
 */
const SAFE_FALLBACK = "/images/site/property.jpg";

type SafeImageSource = string | readonly (string | null | undefined)[] | null | undefined;

interface SafeImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  src: SafeImageSource;
  fallback?: string;
  widths?: number[];
}

function resizeUrl(input: string, width: number): string | null {
  try {
    const url = new URL(input, "https://hivehomes.local");
    if (url.pathname === "/api/_img") {
      url.searchParams.set("w", String(width));
      return url.pathname + url.search;
    }
    if (url.hostname === "images.unsplash.com") {
      url.searchParams.set("auto", url.searchParams.get("auto") ?? "format");
      url.searchParams.set("fit", url.searchParams.get("fit") ?? "crop");
      url.searchParams.set("w", String(width));
      return url.toString();
    }
  } catch {
    return null;
  }
  return null;
}

function srcSetFor(source: string, widths: number[] | undefined): string | undefined {
  if (!widths?.length) return undefined;
  const entries = widths
    .map((width) => {
      const resized = resizeUrl(source, width);
      return resized ? `${resized} ${width}w` : null;
    })
    .filter(Boolean);
  return entries.length > 0 ? entries.join(", ") : undefined;
}

export function SafeImage({
  src,
  fallback = SAFE_FALLBACK,
  alt = "",
  widths,
  srcSet,
  sizes,
  ...rest
}: SafeImageProps) {
  const rawSources = Array.isArray(src) ? src : [src];
  const sources: string[] = [];

  for (const candidate of rawSources) {
    const trimmed = candidate?.trim();
    if (trimmed && !sources.includes(trimmed)) sources.push(trimmed);
  }

  const fallbackSource = fallback?.trim();
  if (fallbackSource && !sources.includes(fallbackSource)) {
    sources.push(fallbackSource);
  }

  const sourceKey = sources.join("\n");
  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    setSourceIndex(0);
  }, [sourceKey]);

  const current = sources[sourceIndex];
  if (!current) return null;

  // `decoding="async"` lets the browser decode JPEG/WebP on a worker
  // thread so it doesn't block the main thread while we're rendering
  // long lists of property cards.
  return (
    <img
      decoding="async"
      {...rest}
      src={current}
      srcSet={srcSet ?? srcSetFor(current, widths)}
      sizes={sizes}
      alt={alt}
      onError={() => setSourceIndex((index) => index + 1)}
    />
  );
}
